import { cx } from '../primitives/cx.js';
import { passthrough } from '../primitives/passthrough.js';
import { Icon } from '../primitives/Icon.tsx';
import { Num } from '../primitives/Num.tsx';

/**
 * StageBar - where a record is in a process, and what it takes to move on.
 *
 * Used by the lead record's status bar and the deal record's stage
 * progression. One component, because "which step are we on" is one question
 * however many objects ask it.
 *
 * Every stage is shown, including the ones already passed and the ones ahead.
 * A bar showing only the current stage tells a person where they are and not
 * where they are going, and the second half is what makes it a process rather
 * than a label.
 */

export interface Stage {
  key: string;
  label: string;
  /** 'won' and 'lost' are ends, and are drawn as ends rather than as steps. */
  outcome?: 'open' | 'won' | 'lost';
  /** Shown under the label when the stage carries one, already formatted. */
  detail?: string | null;
}

export interface StageBarLabels {
  /** Announced on the current stage, since position is not only a colour. */
  current: string;
  done: string;
  /** The accessible name of the whole control. */
  progress: string;
}

export interface StageBarProps {
  stages: Stage[];
  currentKey: string;
  labels: StageBarLabels;
  /** Absent means this reader cannot move the record, so nothing is clickable. */
  onSelect?: ((key: string) => void) | undefined;
}

export function StageBar({
  stages,
  currentKey,
  labels,
  onSelect,
  ...rest
}: StageBarProps & Record<string, unknown>): JSX.Element {
  const currentIndex = stages.findIndex((stage) => stage.key === currentKey);

  return (
    <ol
      {...passthrough(rest)}
      data-component="StageBar"
      aria-label={labels.progress}
      className="flex w-full flex-wrap items-stretch gap-1"
    >
      {stages.map((stage, index) => {
        const done = currentIndex >= 0 && index < currentIndex;
        const current = stage.key === currentKey;
        const lost = stage.outcome === 'lost';
        const won = stage.outcome === 'won';

        const content = (
          <span className="flex min-w-0 flex-col items-start gap-0.5">
            <span className="flex items-center gap-1">
              {/* Colour is never the only carrier: a tick for done, the word
                  for current, and the label itself always present. */}
              {done ? <Icon name="Check" size="inline" decorative /> : null}
              <span className="truncate text-dense">{stage.label}</span>
            </span>
            {stage.detail ? <Num className="text-meta opacity-80">{stage.detail}</Num> : null}
            {current ? <span className="sr-only">{labels.current}</span> : null}
            {done ? <span className="sr-only">{labels.done}</span> : null}
          </span>
        );

        const className = cx(
          'flex min-h-touch flex-1 items-center rounded-control px-3 py-1.5',
          current && !won && !lost && 'bg-bronze text-bronze-on',
          current && won && 'bg-success-tint text-success-text',
          current && lost && 'bg-danger-tint text-danger-text',
          !current && done && 'bg-sunken text-ink-600',
          !current && !done && 'bg-sunken text-ink-400',
        );

        return (
          <li key={stage.key} data-stage={stage.key} data-current={current || undefined} className="flex min-w-0 flex-1">
            {onSelect ? (
              <button
                type="button"
                aria-current={current ? 'step' : undefined}
                onClick={() => onSelect(stage.key)}
                className={cx(
                  className,
                  'focus-visible:outline-none',
                )}
              >
                {content}
              </button>
            ) : (
              <span aria-current={current ? 'step' : undefined} className={className}>
                {content}
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}
